// src/pages/SDA_Owner/Portal.jsx

import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import SideBar from "./components/Sidebar";
import Header from "./components/Header";
import OwnerDashboard from "./components/pages/DashBoard";
import ManageAccommodations from "./components/pages/ManageAccommodations";
import Availability from "./components/pages/Availability";
import SettingsPage from "./components/pages/Settings";
import InquiryPage from "./components/pages/Inquiry"; // ✅ NEW
import Booking from "./components/pages/Booking";

const API_URL = import.meta.env.VITE_API_URL;

function SDAOwnerPortal() {
  const queryClient = useQueryClient();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState("dashboard");
  const [openAddAccommodationFlag, setOpenAddAccommodationFlag] =
    useState(false);

  const [showSecureLoginNotice, setShowSecureLoginNotice] = useState(false);

  useEffect(() => {
    const token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken");

    if (!token) return;

    try {
      const payloadPart = token.split(".")[1];

      if (!payloadPart) return;

      const base64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");

      const payload = JSON.parse(atob(base64));

      const authMethod = payload.auth_method || "";

      const lastNoticeToken = sessionStorage.getItem("secureLoginNoticeToken");

      if (authMethod === "magic_link" && lastNoticeToken !== token) {
        setShowSecureLoginNotice(true);
      }
    } catch (error) {
      console.error("Failed to check secure login method:", error);
    }
  }, []);

  const fetchJSON = async (url) => {
    const token = localStorage.getItem("accessToken");
    if (!token) throw new Error("No access token found");

    const res = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.message || "Prefetch failed");
    return data;
  };

  const prefetchAccommodations = () => {
    if (!API_URL) return;
    queryClient.prefetchQuery({
      queryKey: ["accommodations"],
      queryFn: async () => {
        const data = await fetchJSON(
          `${API_URL}/api/sdaowner/get_accommodations`,
        );
        return data?.accommodations || [];
      },
      staleTime: 60_000,
    });
  };

  const prefetchBookings = () => {
    if (!API_URL) return;
    queryClient.prefetchQuery({
      queryKey: ["bookings"],
      queryFn: async () => {
        const data = await fetchJSON(`${API_URL}/api/sdaowner/get_bookings`);
        return data?.bookings || [];
      },
      staleTime: 60_000,
    });
  };

  const prefetchInquiries = () => {
    if (!API_URL) return;
    queryClient.prefetchQuery({
      queryKey: ["inquiries"],
      queryFn: async () => {
        const data = await fetchJSON(`${API_URL}/api/sdaowner/inquiries`);
        return data?.inquiries || data || [];
      },
      staleTime: 60_000,
    });
  };

  const pageConfig = {
    dashboard: { title: "Dashboard", component: OwnerDashboard },
    accommodations: {
      title: "Manage Accommodations",
      component: ManageAccommodations,
    },
    availability: { title: "Availability", component: Availability },

    //Inquiry
    inquiry: {
      title: "Inquiry",
      component: InquiryPage,
    },

    //Booking
    booking: {
      title: "Booking",
      component: Booking,
    },

    // Settings
    settings: {
      title: "Account Settings",
      component: SettingsPage,
    },
  };

  const { component: CurrentPageComponent, title: pageTitle } =
    pageConfig[currentPage] || pageConfig.dashboard;

  return (
    <div className="min-h-screen flex bg-gray-50 overflow-x-hidden">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-[60] md:hidden"
          onClick={() => setSidebarOpen(false)}></div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-74 bg-white border-r border-gray-200 z-[70] md:z-50 transform transition-transform duration-300
            ${
              sidebarOpen ? "translate-x-0" : "-translate-x-full"
            } md:translate-x-0`}>
        <SideBar
          currentPage={currentPage}
          onNavigate={(page) => {
            setCurrentPage(page);
            setSidebarOpen(false); // close after navigating on mobile
          }}
          onPrefetchAccommodations={prefetchAccommodations}
          onPrefetchBookings={prefetchBookings}
          onPrefetchInquiries={prefetchInquiries}
        />
      </aside>

      {/* Main content */}
      <div className="flex flex-col flex-1 ml-0 md:ml-74 min-w-0">
        {/* Header (with mobile menu button) */}
        <Header
          pageTitle={pageTitle}
          onMenuClick={() => setSidebarOpen(!sidebarOpen)}
          onSettingsClick={() => setCurrentPage("settings")}
          onBellClick={() => setCurrentPage("inquiry")}
        />

        {/* Page content */}
        <main className="flex-1 p-6 overflow-auto">
          <CurrentPageComponent
            // For Dashboard: when "Add Accommodation" is clicked
            onAddAccommodation={() => {
              setCurrentPage("accommodations");
              setOpenAddAccommodationFlag(true);
            }}
            // For Dashboard: "Update Accommodation"
            onGoToAccommodations={() => {
              setCurrentPage("accommodations");
            }}
            onGoToBookings={() => {
              setCurrentPage("booking");
            }}
            // For ManageAccommodations
            openAddFromDashboard={openAddAccommodationFlag}
            onAddHandled={() => setOpenAddAccommodationFlag(false)}
          />
        </main>
      </div>

      {/* Secure login password notice */}
      {showSecureLoginNotice && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50" />

          {/* Modal */}
          <div className="relative z-10 w-full max-w-md rounded-2xl bg-white shadow-xl">
            <div className="px-6 pt-6 pb-4">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-pink-50">
                <span className="text-2xl">🔒</span>
              </div>

              <h2 className="text-center text-xl font-semibold text-gray-900">
                Secure login successful
              </h2>

              <p className="mt-3 text-center text-sm leading-6 text-gray-600">
                You have signed in using a secure password reset link. For your
                account security, please update your password immediately.
              </p>
            </div>

            <div className="px-6 pb-6">
              <button
                type="button"
                onClick={() => {
                  const token =
                    localStorage.getItem("accessToken") ||
                    sessionStorage.getItem("accessToken");

                  if (token) {
                    sessionStorage.setItem("secureLoginNoticeToken", token);
                  }

                  setShowSecureLoginNotice(false);
                  setCurrentPage("settings");
                }}
                className="w-full rounded-full bg-[#D2138C] px-5 py-3 text-sm font-medium text-white transition hover:bg-[#b01173]">
                Change password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SDAOwnerPortal;
